import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

// redux
import { mapDispatchProps, mapStateToProps } from '../../../../store/store';
import { connect } from 'react-redux';
import Button from '../../../../components/button';
import { ROUTES } from '../../../../routes';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import API from '../../../../api';

import OnAfterModalClosed from '../../../../libraries/onAfterModalClosed';
import BsProductUploader from '../bsProductUploader';
import Builder from '../builder';
import ProductSelector from '../productSelector';
import Util from '../util';
import { useLanguage } from '@languages/index';

const SNSUploadYoutube = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.sns };
  const [searchParams, setSearchParams] = useSearchParams();
  const youtubeId = searchParams.get('id');

  const navigate = useNavigate();

  const onAfterModalClosed = new OnAfterModalClosed(props);

  /// state
  const [url, setUrl] = useState('');
  const [products, setProducts] = useState([]);
  const [disabled, setDisabled] = useState(!!youtubeId);

  /// query
  // fetch brand
  const fetchBrandRes = useQuery({
    queryKey: ['fetch_brand'],
    queryFn: async () => {
      try {
        const res = await API.Brand.show({ brandToken: props.auth.token });
        props.setBrand(res.data.brand);

        return res;
      } catch (error) {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_1,
        });
      }
    },
  });

  /// effect
  useEffect(() => {
    if (!youtubeId) return;
    (async () => {
      try {
        // fetch youtube
        const res = await API.BrandSns.fetchYoutubes({
          brandToken: props.auth.token,
          where: { id: youtubeId },
        });
        const youtube = res.data.reviews[0];

        // set states
        setUrl(`https://www.youtube.com/watch?v=${youtube.videoKey}`);
        setProducts(youtube.products);
        setDisabled(false);
      } catch (error) {
        console.log(error);
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.youtube_upload_text_1,
        });
      }
    })();
  }, []);

  /// others
  const _showProductSelectorModal = (initSearchTxt: string = '') => {
    props.showModal({
      isVisible: true,
      hideCloseButton: true,
      content: () => {
        return (
          <ProductSelector
            initSearchTxt={initSearchTxt}
            onSelect={(prd) => {
              setProducts(products.concat(prd));
            }}
            searchProducts={true}
            searchBsProducts={true}
          />
        );
      },
    });
  };

  /// sns install check
  if (
    fetchBrandRes.data &&
    fetchBrandRes.data.brand &&
    fetchBrandRes.data.brand.hasExperiencedSnsInstall &&
    !fetchBrandRes.data.brand.snsPassInstalledAt
  ) {
    navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH);
  }

  /// build products
  const buildProducts = () => {
    return products.map((prd, idx) => (
      <div
        key={prd.id}
        className="my-[10px] w-full flex flex-row justify-items-start items-center"
        style={{ position: 'relative' }}
      >
        <img
          className="object-contain w-[72px] h-[72px] bg-[#D9D9D9]"
          src={Util.makeImageUrl(
            prd.isBsProduct ? 'brand_sns/thumb-sm' : 'product/thumb-sm',
            prd.imageUrl,
          )}
        />
        <div className="flex flex-col gap-1 w-full overflow-hidden px-5">
          <span className="font-medium text-[15px] whitespace-nowrap overflow-hidden overflow-ellipsis">
            {prd.name}
          </span>

          <span
            className="text-black text-[15px] font-normal"
            style={{ wordBreak: 'keep-all' }}
          >
            {languageTexts._wear(prd.sizeName)}
          </span>

          <div className="flex flex-row gap-5 justify-between">
            <a
              className="cursor-pointer text-[15px] text-[#0066FF] underline whitespace-nowrap truncate text-ellipsis overflow-hidden max-w-[350px]"
              onClick={() => {
                Util.onLinkClick(prd.url);
              }}
            >
              {`${Util.getUrlText(prd.url)}`}
            </a>
          </div>
        </div>

        <div className="cursor-pointer">
          <img
            style={{ position: 'absolute', top: '0px', right: '0px' }}
            src="/assets/icons/ic-close-black-squared.svg"
            onClick={(e) => {
              const newProducts = [];
              for (let pi = 0; pi < products.length; ++pi) {
                if (pi !== idx) newProducts.push(products[pi]);
              }
              setProducts(newProducts);
            }}
          />
        </div>
      </div>
    ));
  };

  /// build main
  const build = () => {
    if (!fetchBrandRes.data) return null;

    if (!props.brand?.hasExperiencedSnsInstall || !props.brand?.remainSnsPass)
      return null;

    return (
      <div className="flex flex-col items-center w-[1025px] mx-auto my-[50px]">
        <div className="flex flex-row w-full">
          {Builder.buildBackwardText((e) => {
            navigate(ROUTES.SNS_INDEX.PATH);
          })}
        </div>

        <div style={{ height: '39px' }} />

        <span className="font-bold text-[22px] text-[#000000]">
          {languageTexts.index_text_7}{' '}
          {youtubeId
            ? languageTexts.modify2
            : languageTexts.celeb_upload_text_1}
        </span>

        <div style={{ height: '70px' }} />

        <div className="flex flex-col items-center w-[335px]">
          {Builder.buildTitle('URL')}
          <div style={{ height: '10px' }} />
          {Builder.buildTextInput('URL', [url, setUrl], disabled)}

          <div style={{ height: '46px' }} />

          <div className="flex flex-row w-full">
            {Builder.buildTitle(languageTexts.celeb_detail_text_8)}
            <span className="grow" />
            <span
              className="cursor-pointer underline text-[13px] text-[#000000]"
              onClick={(e) => {
                if (disabled) return;

                _showProductSelectorModal();
              }}
            >
              {languageTexts.celeb_upload_text_5}
            </span>
          </div>
          <div style={{ height: '10px' }} />
          {buildProducts()}

          <div style={{ height: '42px' }} />
          <BsProductUploader
            disabled={disabled}
            onCreate={(prod) => setProducts(products.concat([prod]))}
          />

          <div style={{ height: '50px' }} />

          <Button
            text={languageTexts.celeb_upload_text_6}
            buttonColor={'BLACK'}
            borderColor={'BLACK'}
            textColor={'WHITE'}
            textClassName={'prose text-sm'}
            onClick={async () => {
              if (disabled) return;

              try {
                /// validate
                const validateErros = [];
                if (url.trim().length <= 0)
                  validateErros.push(languageTexts.video_upload_text_3);
                if (products.length <= 0)
                  validateErros.push(languageTexts.celeb_upload_text_8);
                if (validateErros.length > 0) {
                  return props.showModal({
                    hideCloseButton: true,
                    text: validateErros.join('\n'),
                  });
                }

                /// block screen
                props.showModal({
                  hideCloseButton: true,
                  hideConfirmButton: true,
                  text: languageTexts.celeb_upload_text_11,
                });

                /// create bs_products from products
                for (let pi = 0; pi < products.length; ++pi) {
                  const prd = products[pi];
                  if (prd.isBsProduct) continue;

                  // copy image
                  const cpImgRes = await API.BrandSns.copyImageFrom({
                    brandToken: props.auth.token,
                    srcDir: 'product',
                    srcImgName: prd.imageUrl,
                  });

                  // create
                  const res = await API.BrandSns.createBsProduct({
                    brandToken: props.auth.token,
                    productId: prd.id,
                    name: prd.name,
                    colorName: prd.colorName,
                    sizeName: prd.sizeName,
                    url: prd.url,
                    imageUrl: prd.imageUrl,
                  });

                  products[pi] = res.data.result;
                }

                /// create or update BrandSnsCelebrity
                try {
                  if (!youtubeId) {
                    // create
                    const res = await API.BrandSns.createYoutube({
                      brandToken: props.auth.token,
                      videoKey: url,
                      productIds: products.map((p) => p.id),
                    });
                  } else {
                    // update
                    const res = await API.BrandSns.updateYoutube(youtubeId, {
                      brandToken: props.auth.token,
                      videoKey: url,
                      productIds: products.map((p) => p.bsProductId),
                    });
                  }
                } catch (error) {
                  props.showModal({
                    hideCloseButton: true,
                    text: languageTexts.youtube_upload_text_2,
                  });
                  throw error;
                }

                /// show finish modal
                onAfterModalClosed.set(() => {
                  props.showModal({
                    hideCloseButton: true,
                    text: languageTexts.celeb_upload_text_13,
                    confirm: (params) => {
                      navigate(ROUTES.SNS_INDEX.PATH);
                    },
                  });
                });
                props.closeModal(); // close block-screen-modal
              } catch (e) {
                // show err message
                props.showModal({
                  hideCloseButton: true,
                  text: languageTexts.celeb_upload_text_14,
                });
              }
            }}
          />
        </div>
      </div>
    );
  };

  return build();
};

export default connect(mapStateToProps, mapDispatchProps)(SNSUploadYoutube);
